import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, FileText, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface ReplicationViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${
              isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
            }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const ReplicationView: React.FC<ReplicationViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(false);
  const [replicationData, setReplicationData] = useState<any>(null);

  // Legend Hover States
  const [hoveredWrite, setHoveredWrite] = useState<string | null>(null);
  const [hoveredFlush, setHoveredFlush] = useState<string | null>(null);
  const [hoveredReplay, setHoveredReplay] = useState<string | null>(null);
  const [hoveredWal, setHoveredWal] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');
    

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/replication', { params });
      setReplicationData(res.data?.data || res.data);
    } catch (err) {
      console.warn('ReplicationView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels = replicationData?.labels || [];
  const series = replicationData?.series || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
        ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);
  const formattedData = labels.map((_: string, idx: number) => {
    const defaultLag = series.replication_lag_sec?.[idx] ?? 0;
    return {
      time: timeLabels[idx],
      wal_size_mb: series.pg_wal_size_mb?.[idx] ?? 0,
      replication_lag: defaultLag,
      write_lag: series.write_lag_sec?.[idx] ?? defaultLag,
      flush_lag: series.flush_lag_sec?.[idx] ?? defaultLag,
      replay_lag: series.replay_lag_sec?.[idx] ?? defaultLag,
    };
  });

  const hasData = (arr: any[], key: string) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) => typeof item[key] === 'number' && !isNaN(item[key]));
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  const getDot = (_hovered?: any, _key?: any, _color?: any, _defaultR?: any) => false;

  return (
    <div className="space-y-3 relative">
      {loading && (
        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      )}

      <div>
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-orange-500" />
          Replication & WAL Metrics
        </h2>
        <p className="text-xs text-gray-400 mt-1">Tracks data replication processes for maintaining redundancy and high availability across nodes.</p>
      </div>

      {/* 3 Columns Subcharts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
        {/* WAL Write Lag */}
        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">WAL Write Lag by Client</h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData, 'write_lag') ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3}/>
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredWrite} onHoverSeries={setHoveredWrite} />} />
                  <Line
                    type="monotone"
                    dataKey="write_lag"
                    name="Write Lag (sec)"
                    stroke="#3b82f6"
                    strokeOpacity={getOpacity(hoveredWrite, 'write_lag')}
                    strokeWidth={hoveredWrite === 'write_lag' ? 2.5 : 1.5}
                    dot={getDot(hoveredWrite, 'write_lag', '#3b82f6')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No write lag data available</span>
            )}
          </div>
        </div>

        {/* WAL Flush Lag */}
        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">WAL Flush Lag by Client</h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData, 'flush_lag') ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3}/>
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredFlush} onHoverSeries={setHoveredFlush} />} />
                  <Line
                    type="monotone"
                    dataKey="flush_lag"
                    name="Flush Lag (sec)"
                    stroke="#a855f7"
                    strokeOpacity={getOpacity(hoveredFlush, 'flush_lag')}
                    strokeWidth={hoveredFlush === 'flush_lag' ? 2.5 : 1.5}
                    dot={getDot(hoveredFlush, 'flush_lag', '#a855f7')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No flush lag data available</span>
            )}
          </div>
        </div>

        {/* WAL Replay Lag */}
        <div className="">
          <div className="mb-3 border-b border-gray-700/50 pb-2">
            <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">WAL Replay Lag by Client</h4>
          </div>
          <div className="h-48 w-full flex items-center justify-center">
            {hasData(formattedData, 'replay_lag') ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formattedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3}/>
                  <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                  <Legend content={<ScrollableLegend hoveredSeries={hoveredReplay} onHoverSeries={setHoveredReplay} />} />
                  <Line
                    type="monotone"
                    dataKey="replay_lag"
                    name="Replay Lag (sec)"
                    stroke="#ef4444"
                    strokeOpacity={getOpacity(hoveredReplay, 'replay_lag')}
                    strokeWidth={hoveredReplay === 'replay_lag' ? 2.5 : 1.5}
                    dot={getDot(hoveredReplay, 'replay_lag', '#ef4444')}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-xs text-gray-500 italic">No replay lag data available</span>
            )}
          </div>
        </div>
      </div>

      {/* Large Chart: WAL Size */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4 border-b border-gray-700/50 pb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold text-white">Write Ahead Log (WAL) Size</h3>
          </div>
          <span className="text-xs text-gray-400">Past {timeRange} Hours</span>
        </div>
        <div className="h-40 w-full flex items-center justify-center">
          {hasData(formattedData, 'wal_size_mb') ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedData}>
                <defs>
                  <linearGradient id="colorWal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={11} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} tickCount={3}/>
                <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', color: '#fff' }} />
                <Legend content={<ScrollableLegend hoveredSeries={hoveredWal} onHoverSeries={setHoveredWal} />} />
                <Area
                  type="monotone"
                  dataKey="wal_size_mb"
                  name="WAL Size (MB)"
                  stroke="#3b82f6"
                  strokeOpacity={getOpacity(hoveredWal, 'wal_size_mb')}
                  fillOpacity={getOpacity(hoveredWal, 'wal_size_mb') * 0.4}
                  fill="url(#colorWal)"
                  strokeWidth={hoveredWal === 'wal_size_mb' ? 3 : 2}
                  dot={getDot(hoveredWal, 'wal_size_mb', '#3b82f6', 3)}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <span className="text-xs text-gray-500 italic">No WAL size data available</span>
          )}
        </div>
      </div>
    </div>
  );
};
